import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import AnimatedSpinner from '@rover/components/src/typography/AnimatedSpinner';
import { Box, Button, Flex, Heading } from '@rover/kibble/core';
import { SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import { HybridOnPressHandler } from '@rover/kibble/core/utils';
import MQ from '@rover/kibble/styles/MediaQuery';
import Review from '@rover/react-lib/src/components/modals/Review/Review';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import AutomaticRecordContent from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/components/ReviewsSection/AutomaticRecordContent/AutomaticRecordContent';
import AutoHeight from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/utils/AutoHeight/AutoHeight';
import DynamicModal from '@rover/react-lib/src/pages/training/components/DynamicModal';
import { useApiPersonDetailReviewsList } from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';
import { ApiPersonDetailReviewsListServiceTypeCategory as ServiceTypeCategories } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListServiceTypeCategory';
import type { PersonTrainerProfile } from '@rover/rsdk/src/apiClient/latest/model/personTrainerProfile';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { TrainerProfileReadAllReviewsClicked } from '../../../events/analyticsEvents';
import { ReviewsLayout } from '../../PublicTrainerProfilePageLayout';
import FullMixedReviewsList from '../FullMixedReviewsList/FullMixedReviewsList';
import ReviewsModalContext from '../FullMixedReviewsList/ReviewsModalContext';

import ServiceTypeReviewFilter from './ServiceTypeReviewFilter';

export type MixedReviewsSectionProps = {
  trainerInfo: PersonTrainerProfile;
  hasBorderBottom?: boolean;
};

// The Button component doesn't accept responsive fullWidth prop
// eslint-disable-next-line rover/prefer-kibble-components
const StyledButton = styled(Button)`
  ${MQ.XS.toString()} {
    width: 100%;
  }
`;

/** **Note:** This component should always be rendered with the `ReviewsModalContext` above it  */
const MixedReviewsSection = ({
  trainerInfo,
  hasBorderBottom = true,
}: MixedReviewsSectionProps): JSX.Element | null => {
  const hasTrainingReviews = trainerInfo.trainerInsights.servicesWithTextReviews.some(
    (service) => service.slug === 'dog-training'
  );
  const hasOtherServicesReviews = trainerInfo.trainerInsights.servicesWithTextReviews.some(
    (service) => service.slug !== 'dog-training'
  );
  const shouldShowFilter = hasTrainingReviews && hasOtherServicesReviews;

  const defaultCategory = hasTrainingReviews
    ? ServiceTypeCategories['dog-training']
    : ServiceTypeCategories['all-services'];
  const [serviceTypeCategory, setServiceTypeCategory] =
    useState<ServiceTypeCategories>(defaultCategory);

  const { data: reviews, isLoading: isLoadingReviews } = useApiPersonDetailReviewsList(
    trainerInfo.opk,
    {
      page: 1,
      serviceTypeCategory,
    }
  );
  const isMobile = useMatchMedia(MQ.MOBILE.getBreakpointString());
  const {
    selectedReviewIndex,
    isModalForAutomaticRecord,
    openReviewsModal,
    closeReviewsModal,
    toggleAutomaticRecordModal,
    setServiceTypeCategory: setModalServiceTypeCategory,
  } = useContext(ReviewsModalContext);

  const handleClickReadMoreReviews: HybridOnPressHandler = ({ webEvent: event }) => {
    emitAnalyticsEvent(new TrainerProfileReadAllReviewsClicked({ providerOpk: trainerInfo.opk }));
    setModalServiceTypeCategory(serviceTypeCategory);
    openReviewsModal(event);
  };

  const handleClickReadReview = (index: number): void => {
    setModalServiceTypeCategory(serviceTypeCategory);
    openReviewsModal(undefined, index);
  };

  const reviewListSummary = (reviews?.results || []).slice(0, 4);
  const margins: SpacingSystemType = ['0x', '6x', '0x', '6x'];

  const onChangeFilter = (category: ServiceTypeCategories): void => {
    setServiceTypeCategory(category);
  };

  const reviewTitle = (
    <Heading size="400" fontWeight="semibold" mb="0x">
      {/* TODO DEV-104195: Translate "Reviews" */}
      <Box sx={{ fontSize: '26px' }}>Reviews</Box>
    </Heading>
  );

  if (trainerInfo.trainerInsights.servicesWithTextReviews.length === 0) {
    return null;
  }

  const fullList = !isMobile ? !isModalForAutomaticRecord : true;
  // if there is a scrollIndex selected, we want to show the modal
  const modalOpen = selectedReviewIndex >= 0;

  const reviewsSummary = (
    <Flex width="100%" flexDirection={['column', 'row', 'column', 'row']} flexWrap="wrap">
      {reviewListSummary.map((review, index) => (
        <Flex
          width={['100%', '50%', '100%', '50%']}
          key={`${review.url}-${index}`}
          pr={index % 2 === 0 ? margins : '0x'}
          pl={index % 2 === 0 ? '0x' : margins}
          sx={{ paddingBottom: '40px' }}
        >
          <Review
            review={review}
            onReadMore={() => handleClickReadReview(index)}
            onLearnMore={toggleAutomaticRecordModal}
            variant="training"
          />
        </Flex>
      ))}
    </Flex>
  );

  return (
    <ReviewsLayout hasBorderBottom={hasBorderBottom}>
      <Box width="100%" data-testid="reviews-list">
        <Flex
          width="100%"
          flexDirection={['column', 'row', 'column', 'row']}
          justifyContent={['initial', 'space-between', 'initial', 'space-between']}
          gap="3x"
          pb={['8x', '12x', '8x', '12x']}
          pt={['4x', '0x', '4x', '0x']}
        >
          {reviewTitle} {shouldShowFilter && <ServiceTypeReviewFilter onChange={onChangeFilter} />}
        </Flex>
        {isLoadingReviews ? (
          <Flex width="100%" justifyContent="center" pb={['8x', '12x', '8x', '12x']}>
            <AnimatedSpinner />
          </Flex>
        ) : (
          reviewsSummary
        )}
        <StyledButton
          onPress={handleClickReadMoreReviews}
          sx={{
            '&:focus': {
              backgroundColor: 'initial',
            },
          }}
        >
          {/* TODO DEV-104195: Translate "Read more reviews" */}
          Read more reviews
        </StyledButton>
        <DynamicModal isOpen={modalOpen} onRequestClose={closeReviewsModal} isFullHeight>
          <AutoHeight>
            {isModalForAutomaticRecord &&
              modalOpen &&
              (isMobile ? (
                <DynamicModal isOpen onRequestClose={toggleAutomaticRecordModal}>
                  <AutomaticRecordContent onClose={toggleAutomaticRecordModal} />
                </DynamicModal>
              ) : (
                <AutomaticRecordContent onBack={toggleAutomaticRecordModal} />
              ))}
            {fullList && (
              <FullMixedReviewsList
                scrollIndex={selectedReviewIndex}
                trainerProfile={trainerInfo}
                opk={trainerInfo.opk}
                onLearnMore={toggleAutomaticRecordModal}
                onClickClose={closeReviewsModal}
                shouldShowFilter={shouldShowFilter}
              />
            )}
          </AutoHeight>
        </DynamicModal>
        <DynamicModal
          isOpen={isModalForAutomaticRecord && !modalOpen}
          onRequestClose={toggleAutomaticRecordModal}
        >
          <AutomaticRecordContent onClose={toggleAutomaticRecordModal} />
        </DynamicModal>
      </Box>
    </ReviewsLayout>
  );
};

export default MixedReviewsSection;
